import { useState, useEffect, useCallback } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { type Dayjs } from "dayjs";
import { useRestQuery } from "../../../hooks/useRestQuery";
import { listStudents } from "../../../api/students";
import type { ClassRoomPublic, StudentPublic } from "../../../api/types";
import type { CreateObservationPayload, ObservationCategory, ObservationSentiment } from "../types";
import { useTranslationsContext } from "../../../contexts/TranslationsContext";
import { datePickerPopperSlots, datePickerSx, logifyInputSx } from "../utils/pickerTheme";

const CATEGORIES: ObservationCategory[] = ["ACADEMIC", "SOCIAL", "BEHAVIOR", "COMMUNICATION", "PROGRESS"];
const SENTIMENTS: ObservationSentiment[] = ["POSITIVE", "NEUTRAL", "ATTENTION"];

const CATEGORY_COLORS: Record<string, string> = {
  ACADEMIC: "var(--color-category-academic)",
  SOCIAL: "var(--color-category-social)",
  BEHAVIOR: "var(--color-category-behavior)",
  COMMUNICATION: "var(--color-category-communication)",
  PROGRESS: "var(--color-category-progress)",
};

const SENTIMENT_COLORS: Record<string, string> = {
  POSITIVE: "var(--color-sentiment-positive)",
  NEUTRAL: "var(--color-sentiment-neutral)",
  ATTENTION: "var(--color-sentiment-attention)",
};

function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface ObservationFormProps {
  classRooms: ClassRoomPublic[];
  initialData?: Partial<CreateObservationPayload>;
  onSubmit: (payload: CreateObservationPayload) => void;
  onCancel: () => void;
  loading?: boolean;
}

export default function ObservationForm({ classRooms, initialData, onSubmit, onCancel, loading }: ObservationFormProps) {
  const { translation } = useTranslationsContext();

  const [classRoomId, setClassRoomId] = useState<string>(initialData?.ClassRoomId ? String(initialData.ClassRoomId) : "");
  const [studentId, setStudentId] = useState<string>(initialData?.StudentId ? String(initialData.StudentId) : "");
  const [category, setCategory] = useState<ObservationCategory | "">(initialData?.category || "");
  const [sentiment, setSentiment] = useState<ObservationSentiment | "">(initialData?.sentiment || "");
  const [text, setText] = useState(initialData?.text || "");
  const [date, setDate] = useState(initialData?.date || todayStr());

  // Fetch students when classroom changes
  const { data: students } = useRestQuery<StudentPublic[]>(
    () => listStudents({ ClassRoomId: classRoomId }),
    {
      key: "logify-form-students",
      deps: [classRoomId],
      skip: !classRoomId,
    }
  );

  // Reset student when classroom changes
  useEffect(() => {
    if (!initialData?.StudentId) setStudentId("");
  }, [classRoomId, initialData?.StudentId]);

  const canSubmit = classRoomId && studentId && category && sentiment && text.trim() && date && !loading;

  const handleSubmit = useCallback(() => {
    if (!canSubmit) return;
    onSubmit({
      ClassRoomId: Number(classRoomId),
      StudentId: Number(studentId),
      category: category as ObservationCategory,
      sentiment: sentiment as ObservationSentiment,
      text: text.trim(),
      date,
    });
  }, [canSubmit, classRoomId, studentId, category, sentiment, text, date, onSubmit]);

  // Pill button helper
  const PillButton = ({ label, color, selected, onClick }: { label: string; color: string; selected: boolean; onClick: () => void }) => (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      sx={{
        px: 1.5,
        py: 0.5,
        borderRadius: "16px",
        border: `1.5px solid ${selected ? color : "rgba(255,255,255,0.12)"}`,
        background: selected ? `${color}22` : "transparent",
        color: selected ? color : "rgba(255,255,255,0.5)",
        fontWeight: selected ? 600 : 400,
        fontSize: "0.78rem",
        cursor: "pointer",
        transition: "all 0.15s",
        outline: "none",
        "&:hover": { borderColor: color, color },
      }}
    >
      {label}
    </Box>
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {/* Classroom select */}
      <TextField
        select
        label={translation.LOGIFY_FORM_CLASS?.defaultMessage || "Class"}
        value={classRoomId}
        onChange={(e) => setClassRoomId(e.target.value)}
        size="small"
        fullWidth
        sx={logifyInputSx}
      >
        {classRooms.map((cr) => (
          <MenuItem key={cr.id} value={cr.id}>
            {cr.name}{cr.subject ? ` — ${cr.subject}` : ""}
          </MenuItem>
        ))}
      </TextField>

      {/* Student select */}
      <TextField
        select
        label={translation.LOGIFY_FORM_STUDENT?.defaultMessage || "Student"}
        value={studentId}
        onChange={(e) => setStudentId(e.target.value)}
        size="small"
        fullWidth
        disabled={!classRoomId || !students?.length}
        sx={logifyInputSx}
      >
        {(students || []).map((s) => (
          <MenuItem key={s.id} value={s.id}>
            {s.name}
          </MenuItem>
        ))}
      </TextField>

      {/* Category pills */}
      <Box>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", mb: 0.5, display: "block", fontSize: "0.72rem" }}>
          {translation.LOGIFY_FORM_CATEGORY?.defaultMessage || "Category"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
          {CATEGORIES.map((cat) => (
            <PillButton
              key={cat}
              label={translation[`LOGIFY_CATEGORY_${cat}`]?.defaultMessage || cat}
              color={CATEGORY_COLORS[cat]}
              selected={category === cat}
              onClick={() => setCategory(cat)}
            />
          ))}
        </Box>
      </Box>

      {/* Sentiment pills */}
      <Box>
        <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", mb: 0.5, display: "block", fontSize: "0.72rem" }}>
          {translation.LOGIFY_FORM_SENTIMENT?.defaultMessage || "Assessment"}
        </Typography>
        <Box sx={{ display: "flex", gap: 0.8 }}>
          {SENTIMENTS.map((s) => (
            <PillButton
              key={s}
              label={translation[`LOGIFY_SENTIMENT_${s}`]?.defaultMessage || s}
              color={SENTIMENT_COLORS[s]}
              selected={sentiment === s}
              onClick={() => setSentiment(s)}
            />
          ))}
        </Box>
      </Box>

      {/* Text textarea */}
      <TextField
        label={translation.LOGIFY_FORM_TEXT?.defaultMessage || "Observation"}
        value={text}
        onChange={(e) => setText(e.target.value)}
        multiline
        minRows={2}
        maxRows={6}
        size="small"
        fullWidth
        inputProps={{ maxLength: 10000 }}
        sx={logifyInputSx}
      />

      {/* Date */}
      <DatePicker
        label={translation.LOGIFY_FORM_DATE?.defaultMessage || "Date"}
        value={dayjs(date)}
        onChange={(v: Dayjs | null) => v && setDate(v.format("YYYY-MM-DD"))}
        slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx }, ...datePickerPopperSlots }}
      />

      {/* Actions — MUI Button components */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          size="small"
          sx={{
            color: "rgba(255,255,255,0.6)",
            borderColor: "rgba(255,255,255,0.15)",
            textTransform: "none",
            "&:hover": { borderColor: "rgba(255,255,255,0.3)" },
          }}
        >
          {translation.CANCEL?.defaultMessage || "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={!canSubmit}
          sx={{
            background: "var(--color-logify-primary)",
            textTransform: "none",
            fontWeight: 600,
            "&:hover": { background: "#8568b8" },
            "&.Mui-disabled": { background: "rgba(150, 120, 200, 0.3)", color: "rgba(255,255,255,0.3)" },
          }}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {translation.SAVE?.defaultMessage || "Save"}
        </Button>
      </Box>
    </Box>
  );
}
